import type { PropsWithChildren } from 'react';
import React from 'react';
import type { ModalProps as RNModalProps } from 'react-native';
import { Modal as RNModal, Pressable, StyleSheet } from 'react-native';
import { Column, Row } from './box';
import { Text } from './text';
import type { ResponsiveValue } from '@shopify/restyle';

import { IconX } from '@tabler/icons-react-native';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Theme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ModalProps extends Omit<RNModalProps, 'onDismiss' | 'onRequestClose' | 'visible'> {
  onClose?: () => void;
  open?: boolean;
  columnProps?: React.ComponentProps<typeof Column>;
}

export function Modal({ children, onClose, open, columnProps, ...rest }: ModalProps) {
  const { isLargeUI } = useUISize();

  return (
    <RNModal {...rest} onDismiss={onClose} onRequestClose={onClose} visible={open}>
      {rest.transparent ? (
        <Column alignItems="center" backgroundColor="surface.overlay" flex={1} justifyContent="center" {...columnProps}>
          {isLargeUI ? <Pressable onPress={onClose} style={StyleSheet.absoluteFill} /> : null}
          {children}
        </Column>
      ) : (
        children
      )}
    </RNModal>
  );
}

Modal.Card = function ModalCard({
  children,
  gap = 'spacing.general.x8',
}: PropsWithChildren & {
  gap?: ResponsiveValue<keyof Theme['spacing'], Theme['breakpoints']>;
}) {
  const { isLargeUI } = useUISize();

  return (
    <Column
      backgroundColor="surface.default"
      borderRadius="radius.lg"
      gap={gap}
      maxHeight="85%"
      maxWidth={isLargeUI ? 552 : 400}
      padding="spacing.general.x4"
      width="95%"
    >
      {children}
    </Column>
  );
};

Modal.Title = function ModalTitle({
  allowClose,
  onClose,
  children,
}: { allowClose?: boolean; onClose?: () => void } & PropsWithChildren) {
  const { isLargeUI } = useUISize();
  return (
    <Column
      style={[
        {
          backgroundColor: isLargeUI ? theme.colors['surface.default'] : theme.colors['surface.tertiary-neutral'],
          borderTopLeftRadius: theme.borderRadii['radius.lg'],
          borderTopRightRadius: theme.borderRadii['radius.lg'],
          marginTop: -theme.spacing['spacing.general.x4'],
          marginLeft: -theme.spacing['spacing.general.x4'],
          marginRight: -theme.spacing['spacing.general.x4'],
          padding: theme.spacing['spacing.general.x4'],
        },
        isLargeUI && { borderBottomWidth: 2, borderBottomColor: theme.colors['surface.disabled'] },
      ]}
    >
      <Row justifyContent={isLargeUI ? 'flex-start' : 'center'}>
        {allowClose ? (
          <Pressable
            style={[
              {
                position: 'absolute',
                zIndex: 1,
              },
              isLargeUI ? { right: 0 } : { left: 0 },
            ]}
            onPress={onClose}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.primary']} size={24} />
          </Pressable>
        ) : null}
        <Text textAlign="center" variant="headerXSmall">
          {children}
        </Text>
      </Row>
    </Column>
  );
};
